import type { RelatedTx, SpanRecord, TxSummary } from '@apm/contracts';
import { HOUR_MS } from '@apm/contracts';
import { memo, useMemo } from 'react';
import { useAccess } from '../auth/access';
import { TX_TYPE_TONE } from '../live/format';
import { type DetailOrigin, navigate, originRoute, routeHref } from '../router';
import { serviceLink } from '../services/service-filter';
import { STATUS_LEVEL_CHIP, STATUS_LEVEL_TEXT, statusLevel, statusText } from '../status';
import { TIP, TX_TYPE_LABEL } from '../terms';
import { formatBytes, formatDuration, formatFull, formatRelative } from '../time';
import {
  Button,
  Card,
  Cell,
  Chip,
  type Column,
  EmptyState,
  Loading,
  Page,
  Row,
  Section,
  Table,
  Tooltip,
  Value,
} from '../ui';
import { DbEvents } from './DbEvents';
import { NearbyRequests } from './NearbyRequests';
import { SpanBars } from './SpanBars';
import { SqlTable } from './SqlTable';
import { spansOfKind } from './timeline';
import { useTxDetail } from './useTxDetail';

/**
 * 요청 상세 (7회차 결정 3).
 *
 * 위에서부터 요약 → 구간 막대 → DB 조회 전체 목록 → 외부 호출 → DB 사건.
 * X-View 점, 끌어 고른 목록 행, 검색 결과 행, 오류 목록 행이 모두 여기로 들어온다.
 *
 * 19회차에 "목록으로"가 **온 곳으로** 돌아가고(콘솔 검토 C-5), 같은 경로의 최근 요청으로
 * 건너뛰는 길(C-4)과 클라이언트 IP·브라우저·프로세스·예외 메시지가 붙었다.
 * 22-B회차에 같은 사용자의 앞뒤 요청(빠진 정보 4), 트랜잭션 제어 접기, 잘린 SQL 표시가 붙었다.
 */

/** 어디서 왔는지에 따라 돌아갈 곳이 다르다 (콘솔 검토 C-5). 하위 탭은 메뉴 이름까지 적는다 */
const BACK_LABEL: Record<DetailOrigin, string> = {
  requests: '요청으로',
  errors: '진단·오류로',
  dbevents: '진단·DB 감시로',
  alerts: '알림으로',
  stats: '통계로',
};

export function DetailPage({
  traceId,
  from,
  back,
  service,
  txId,
  at,
}: {
  traceId: string;
  from?: DetailOrigin;
  /** 온 화면의 조건. 돌아가기가 그대로 되살린다 (23-A회차 리뷰 3) */
  back?: string;
  /** 이 상세가 읽을 서비스 줄. 헤더의 전역 선택을 바꿔도 이 값은 바뀌지 않는다. */
  service?: string;
  /** 같은 서비스에 줄이 둘일 때 읽을 실행 ID (D41-5) */
  txId?: string;
  /** 실행 시작 시각. 주소에 `at=`이 있을 때만 있다. 서버는 `txId`와 함께일 때만 쓴다 (59회차) */
  at?: number;
}) {
  const { loading, detail, error, stale } = useTxDetail(traceId, service, txId, at);
  // 다른 요청(또는 같은 요청의 다른 서비스 줄)으로 옮기면 새 응답이 올 때까지 조회 훅이 앞 응답을
  // 들고 있다. 그것을 새 주소 아래 그리면 앞 요청의 요약 아래 새 요청의 DB 사건이 붙는다 (44회차).
  // 앞 주소의 답이면(`stale`) 주소가 바뀐 첫 그림부터 그리지 않는다
  const current = stale ? undefined : detail;

  return (
    <Page>
      <div>
        <Button
          size="sm"
          onClick={() => {
            // 온 곳을 알면 그리로, 모르면 브라우저 이력을 한 칸 되돌린다
            if (from !== undefined) navigate(originRoute(from, back));
            else if (window.history.length > 1) window.history.back();
            else navigate({ name: 'requests' });
          }}
        >
          ← {from === undefined ? '뒤로' : BACK_LABEL[from]}
        </Button>
      </div>

      {loading || stale ? <Loading /> : null}
      {error !== undefined ? <Card tone="error">{error}</Card> : null}
      {current ? (
        // 다른 요청으로 옮기면 접힘·펼침 같은 화면 상태를 새로 시작한다
        <Loaded
          key={`${traceId} ${service ?? ''} ${txId ?? ''}`}
          summary={current.summary}
          spans={current.spans}
          traceId={traceId}
          fromMemory={current.fromMemory}
          related={current.related}
          truncated={current.truncated === true}
        />
      ) : null}
    </Page>
  );
}

/**
 * 받은 상세 한 벌. 같은 응답이면 다시 그리지 않고, 다시 그려도 종류별 거르기를 되풀이하지 않는다 (D-C2).
 * `spansOfKind`가 매번 새 배열을 주면 `SqlTable`의 메모가 매번 무효가 된다
 */
const Loaded = memo(function Loaded({
  summary,
  spans,
  traceId,
  fromMemory,
  related,
  truncated,
}: {
  summary: TxSummary;
  spans: SpanRecord[];
  traceId: string;
  fromMemory: boolean;
  related: RelatedTx[];
  truncated: boolean;
}) {
  const sql = useMemo(() => spansOfKind(spans, 'sql'), [spans]);
  const ext = useMemo(() => spansOfKind(spans, 'ext'), [spans]);
  // DB 사건은 DB 감시의 자리라 관리자만 본다 (52회차). 서버도 일반 계정에 403을 준다
  const { admin } = useAccess();

  return (
    <>
      <Summary row={summary} fromMemory={fromMemory} truncated={truncated} />

      <RelatedRequests current={summary} traceId={traceId} rows={related} />

      <Section boxed title="구간별 소요시간" note="요청 시작을 0으로 둔 시간축. 오류 구간은 빨강">
        <SpanBars spans={spans} fromMemory={fromMemory} />
      </Section>

      <Section
        boxed
        title="SQL"
        note={[
          '요청 안에서 실행된 쿼리 전부. 성공한 것도 보인다.',
          '같은 문장은 묶어 볼 수 있고, 트랜잭션 제어·세션 설정 문장은 접어 둔다',
        ]}
      >
        <SqlTable spans={sql} />
      </Section>

      <Section
        boxed
        title="같은 사용자의 앞뒤 요청"
        note={[
          '같은 클라이언트 IP와 브라우저(User-Agent)에서 앞뒤 1분 안에 보낸 요청.',
          '이 요청은 굵게 표시한다',
        ]}
      >
        <NearbyRequests current={summary} />
      </Section>

      <Section boxed title="외부 호출" note="이 요청이 보낸 외부 HTTP 호출">
        <ExtTable spans={ext} />
      </Section>

      {admin ? (
        <Section
          boxed
          title="DB 사건"
          note={[
            'DB 감시가 기록한 잠금 대기·막힘·유휴 트랜잭션.',
            '요청 구간에는 남지 않는 값이다',
          ]}
        >
          <DbEvents traceId={traceId} />
        </Section>
      ) : null}
    </>
  );
});

function RelatedRequests({
  current,
  traceId,
  rows,
}: {
  current: TxSummary;
  traceId: string;
  rows: readonly RelatedTx[];
}) {
  if (rows.length === 0) return null;
  return (
    <Section boxed title="이어진 서비스 요청" note="같은 요청 번호를 이어받아 처리한 다른 요청">
      <div className="grid gap-2 sm:grid-cols-2">
        {rows.map((row) => {
          const relation =
            row.service === current.service
              ? '같은 서비스의 다른 요청'
              : current.parentService === row.service
                ? '부른 쪽 서비스'
                : '이 요청이 부른 서비스';
          // 실행 ID까지 싣는다. 같은 서비스 줄이 둘이면 서비스만으로는 그 줄이 안 열린다 (D41-5).
          // 시작 시각(`at`)도 싣는다 (59회차 이월). 이어진 줄에는 제 시작 시각이 없어(계약 무변경)
          // 같은 요청 번호인 지금 줄의 것을 쓴다. 서버는 그 앞뒤 날만 먼저 보고, 못 찾으면 전 기간을 본다
          const route = {
            name: 'detail' as const,
            traceId,
            from: 'requests' as const,
            service: row.service,
            txId: row.txId,
            at: current.startedAt,
          };
          return (
            <a
              key={row.txId}
              {...serviceLink(row.service, routeHref(route))}
              className="rounded-xl border border-hair bg-panel px-4 py-3 no-underline hover:bg-band"
            >
              <span className="text-xs text-muted">{relation}</span>
              <span className="mt-1 block font-medium text-link">{row.service}</span>
              <span className="mt-1 block truncate font-mono text-xs text-muted" title={row.entry}>
                {row.entry} · {formatDuration(row.durationMs)}
              </span>
            </a>
          );
        })}
      </div>
    </Section>
  );
}

function Summary({
  row,
  fromMemory,
  truncated,
}: {
  row: TxSummary;
  fromMemory: boolean;
  truncated: boolean;
}) {
  const level = statusLevel(row);
  // 같은 경로의 최근 요청으로 건너뛰는 길 (콘솔 검토 C-4). 이 요청을 가운데 두고 앞뒤 1시간
  const nearby = new URLSearchParams({
    entry: row.entry,
    from: String(row.startedAt - HOUR_MS),
    to: String(row.startedAt + HOUR_MS),
  }).toString();

  return (
    <section className="rounded-xl border border-hair bg-band p-5">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="break-all font-mono text-xl font-semibold text-ink">{row.entry}</h2>
          <p className="mt-1 text-sm text-muted" title={formatFull(row.startedAt)}>
            {formatFull(row.startedAt)} · {formatRelative(row.startedAt)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Chip tone={TX_TYPE_TONE[row.txType]}>{TX_TYPE_LABEL[row.txType]}</Chip>
          <span
            className={`inline-flex rounded-full px-2 py-0.5 text-[11px] ring-1 ring-inset ${STATUS_LEVEL_CHIP[level]}`}
          >
            {statusText(row)}
          </span>
          <a href={routeHref({ name: 'requests', query: nearby })} className="no-underline">
            <Chip tone="neutral">같은 경로 앞뒤 1시간 검색</Chip>
          </a>
        </div>
      </div>

      {fromMemory ? (
        <p className="mb-4 whitespace-pre-line rounded-lg bg-panel px-3 py-2 text-sm text-muted">
          {TIP.fromMemory}
        </p>
      ) : null}

      {/* 파일 수 상한에 걸린 조회 (D42-10). 메모리 안내와 같은 자리·같은 모양이다 */}
      {truncated ? (
        <p className="mb-4 whitespace-pre-line rounded-lg bg-panel px-3 py-2 text-sm text-fail">
          {TIP.detailTruncated}
        </p>
      ) : null}

      <dl className="tabular grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
        <Value label="응답시간" value={formatDuration(row.durationMs)} />
        <Value label="상태" value={statusText(row)} tone={STATUS_LEVEL_TEXT[level]} />
        <Value
          label="DB 오류"
          value={
            row.sqlErrorCount > 0
              ? `${row.sqlErrorCount}건${row.sqlErrorCode ? ` (${row.sqlErrorCode})` : ''}`
              : '없음'
          }
          tone={row.sqlErrorCount > 0 ? 'text-error' : undefined}
          hint={row.sqlErrorCode ? TIP.sqlErrorCode : undefined}
        />
        <Value label="SQL" value={`${row.sqlCount}회 ${formatDuration(row.sqlTimeMs)}`} />
        <Value label="외부 호출" value={formatDuration(row.extTimeMs)} />
        <Value label="예외 종류" value={row.errorType ?? '없음'} />
        <Value label="회사(테넌트)" value={row.tenantId ?? '없음'} hint={TIP.tenant} />
        <Value label="서비스" value={row.service} />
        <Value label="인스턴스" value={row.instance ?? '없음'} mono hint={TIP.instance} />
        <Value label="클라이언트 IP" value={row.clientAddress ?? '없음'} mono />
        <Value
          label="응답 크기"
          value={row.responseBytes === undefined ? '없음' : formatBytes(row.responseBytes)}
          hint={TIP.responseBytes}
        />
        <Value label="요청 ID" value={row.traceId} mono hint={TIP.traceId} />
        <Value label="실행 ID" value={row.txId} mono hint={TIP.txId} />
      </dl>

      {row.errorMessage !== undefined ? (
        <div className="mt-4">
          <p className="text-xs text-muted">예외 메시지</p>
          <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-words rounded-lg bg-error/5 p-3 font-mono text-xs leading-relaxed text-error">
            {row.errorMessage}
          </pre>
        </div>
      ) : null}

      {row.errorFrames !== undefined && row.errorFrames.length > 0 ? (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-muted">
            <Tooltip text={TIP.errorFrames}>오류가 난 자리</Tooltip> ({row.errorFrames.length}줄)
          </summary>
          <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-words rounded-lg bg-band p-3 font-mono text-xs leading-relaxed text-ink">
            {row.errorFrames.join('\n')}
          </pre>
        </details>
      ) : null}

      {row.referer !== undefined ? (
        <div className="mt-3">
          <p className="text-xs text-muted">
            <Tooltip text={TIP.referer}>보낸 화면</Tooltip>
          </p>
          <p className="mt-1 break-all font-mono text-xs text-muted">{row.referer}</p>
        </div>
      ) : null}

      {row.userAgent !== undefined ? (
        <div className="mt-3">
          <p className="text-xs text-muted">
            <Tooltip text={TIP.userAgent}>User-Agent</Tooltip>
          </p>
          <p className="mt-1 break-all font-mono text-xs text-muted">{row.userAgent}</p>
        </div>
      ) : null}
    </section>
  );
}

const EXT_COLUMNS: readonly Column[] = [
  { label: '대상' },
  { label: '소요', width: 'w-28' },
  { label: '상태', width: 'w-28' },
];

function ExtTable({ spans }: { spans: readonly SpanRecord[] }) {
  if (spans.length === 0) {
    return <EmptyState title="외부 호출이 없다" />;
  }
  return (
    <Table columns={EXT_COLUMNS} caption="외부 호출" minWidth="min-w-[560px]">
      {spans.map((span) => {
        const failed = span.status === 'error';
        return (
          <Row key={span.spanId}>
            <Cell mono className="break-all">
              {span.target ?? span.name}
            </Cell>
            <Cell mono nowrap className="tabular">
              {formatDuration(span.durationMs)}
            </Cell>
            <Cell nowrap tone={failed ? 'error' : 'muted'}>
              {failed ? '실패' : '성공'}
            </Cell>
          </Row>
        );
      })}
    </Table>
  );
}
